<template>
  <div>
    <b-button
      v-if="isEligible"
      ref="button__back-previous"
      variant="outline-primary"
      class="button__back-previous ml-u8 mb-u10"
      @click="$_pageNavigationMixin_gotoPreviousPage"
    >
      <span class="mr-2">
        <font-awesome-icon icon="arrow-left" />
      </span>
      Back to previous page
    </b-button>
    <b-container>
      <div v-if="isLoading" class="text-center m-auto">
        <b-spinner v-if="isLoading" label="Loading..." variant="dark" />
      </div>

      <b-form v-else ref="formProfileName" @submit.prevent="submit">
        <b-row class="justify-content-center">
          <b-col cols="12" xs="12" sm="12" md="10" lg="6">
            <div class="text__title h2 font-weight-bold text-left mt-u8 mb-u8">
              {{ textData.title }}
            </div>
            <div v-if="errorMessage" class="text__error">{{ errorMessage }}</div>

            <!-- Name -->
            <div class="mt-u4">
              <label for="input__name" class="mb-u2"> {{ textData.name }} </label>
              <b-form-input
                id="input__name"
                ref="input__firstname"
                v-model="form.firstname"
                :state="errorState('firstname')"
                class="input__name"
                placeholder="Enter your name"
                :disabled="isNotAvailableField('firstname')"
              />
              <div
                v-if="isShowRequiedFeedbackText('firstname')"
                id="input__firstname--invalid-feedback"
                class="text_feedback"
              >
                Name is required.
              </div>
            </div>

            <!-- Surname -->
            <div class="mt-u4">
              <label for="input__surname" class="mb-u2"> {{ textData.surname }} </label>
              <b-form-input
                id="input__surname"
                ref="input__lastname"
                v-model="form.lastname"
                :state="errorState('lastname')"
                class="input__surname"
                placeholder="Enter your surname"
                :disabled="isNotAvailableField('lastname')"
              />
              <div
                v-if="isShowRequiedFeedbackText('lastname')"
                id="input__lastname--invalid-feedback"
                class="text_feedback"
              >
                Surname is required.
              </div>
            </div>
            <b-button
              v-if="isEligible"
              variant="outline-primary"
              class="mt-u5 mb-u5"
              @click="isShowPreview = true"
            >
              Preview Certificate Sample
            </b-button>

            <!-- Important Note -->
            <div v-if="isEligible">
              <div>{{ textData.importantNote }}</div>
              <ul>
                <li class="text__note--list">{{ textData.note1 }}</li>
                <li class="text__note--list">{{ textData.note2 }}</li>
              </ul>
            </div>

            <!-- Button -->
            <div v-if="isEligible" class="d-flex mt-u5 mb-u10">
              <b-button
                variant="success"
                class="button__confirm"
                type="submit"
                :disabled="isSaving"
              >
                <b-spinner v-if="isSaving" style="width: 1rem; height: 1rem" class="" />
                <div v-else>Confirm</div>
              </b-button>
              <b-button
                variant="link"
                class="ml-u1"
                @click="$_pageNavigationMixin_gotoPreviousPage"
              >
                Cancel
              </b-button>
            </div>
            <div v-else class="d-flex mt-u5 mb-u10">
              <b-button
                variant="link"
                class="mt-u5"
                @click="$_pageNavigationMixin_gotoPreviousPage"
              >
                Back
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </b-container>

    <ModalConfirmChangeName
      v-if="isEligible"
      ref="confirm-modal"
      :fullname="`${form.firstname} ${form.lastname}`"
      @confirm="save"
    />

    <modal-certificate-example
      v-if="isEligible"
      ref="preview-certificate-modal"
      v-model="isShowPreview"
      :firstname="form.firstname"
      :lastname="form.lastname"
    />
  </div>
</template>

<script>
import User from '@/models/User';
import ChangeRequest from '@/models/ChangeRequest';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { required } from 'vuelidate/lib/validators';

import ModalCertificateExample from './components/ModalCertificateExample.vue';
import ModalConfirmChangeName from './components/ModalConfirmChangeName.vue';
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  components: {
    ModalCertificateExample,
    ModalConfirmChangeName,
    FontAwesomeIcon
  },
  mixins: [userInfoMixin, pageNavigationMixin],
  props: {
    fallback: {
      type: String,
      default: '/profile'
    }
  },
  data() {
    return {
      textData: {
        title: 'Edit Name',
        name: 'Name',
        surname: 'Surname',
        email: 'Email',
        description: 'Description',
        button: 'Save',
        cancelButton: 'Cancel',
        confirmButton: 'Confirm',
        importantNote: 'Important Note:',
        note1: 'The name and surname in the form will be displayed on the certificate.',
        note2: 'Your profile name can only be changed once every 60 days.'
      },
      disableFields: {
        firstname: false,
        lastname: false
      },
      oldData: {
        firstname: '',
        lastname: ''
      },
      form: {
        firstname: '',
        lastname: ''
      },
      isSaving: false,
      isLoading: false,
      errorMessage: '',
      isEligible: false,
      isShowPreview: false
    };
  },
  validations() {
    return {
      form: {
        firstname: {
          required,
          notSameAs: (value) => {
            if (this.form.lastname === this.oldData.lastname) {
              return value !== this.oldData.firstname;
            }
            return true;
          }
        },
        lastname: {
          required,
          notSameAs: (value) => {
            if (this.form.firstname === this.oldData.firstname) {
              return value !== this.oldData.lastname;
            }
            return true;
          }
        }
      }
    };
  },
  created() {
    this.init();
  },

  methods: {
    async init() {
      this.isLoading = true;

      await Promise.all([User.api().fetchAuth(), this.checkIsEligible()]);

      this.updateUserData();

      this.isLoading = false;
    },

    updateUserData() {
      const { firstname, lastname } = this.user;

      this.$set(this.form, 'firstname', firstname);
      this.$set(this.form, 'lastname', lastname);
      this.$set(this.oldData, 'firstname', firstname);
      this.$set(this.oldData, 'lastname', lastname);
    },

    async save() {
      this.$refs['confirm-modal'].hide();

      this.isSaving = true;

      // Call API to save the data
      try {
        await ChangeRequest.api().changeName(this.form);

        await User.api().fetchAuth();

        this.$toast.success(`Profile name has been updated successfully`);

        this.$_pageNavigationMixin_gotoPreviousPage();
      } catch (error) {
        this.$set(this, 'isEligible', false);

        this.$set(this.form, 'firstname', this.oldData.firstname);
        this.$set(this.form, 'lastname', this.oldData.lastname);
        this.$set(this.disableFields, 'firstname', true);
        this.$set(this.disableFields, 'lastname', true);
        this.$v.$reset();

        this.errorMessage = error.response.data.message;
      } finally {
        this.isSaving = false;
      }
    },
    async submit() {
      this.errorMessage = '';

      await this.checkIsEligible();

      if (!this.isEligible) {
        return;
      }

      if (!(this.$v.form.firstname.notSameAs || this.$v.form.lastname.notSameAs)) {
        this.errorMessage = 'Changes required, please make a change in at least one field.';
      }

      this.$v.$touch();

      if (
        this.errorMessage?.length ||
        this.$v.form.$invalid ||
        this.$v.form.firstname.$invalid ||
        this.$v.form.lastname.$invalid
      )
        return;

      this.$refs['confirm-modal'].show();
    },

    async checkIsEligible() {
      const {
        response: { data }
      } = await ChangeRequest.api().isChangeNameAble();

      if (!data.eligible) {
        await User.api().fetchAuth();

        this.updateUserData();
      }

      this.isEligible = data.eligible;

      if (!this.isEligible) {
        this.errorMessage = `The name can be changed again on ${data.canChangeDate}`;

        this.$set(this.disableFields, 'firstname', true);

        this.$set(this.disableFields, 'lastname', true);
      }
    },

    errorState(key) {
      return this.$v.form[key].$dirty
        ? this.$v.form[key].$anyError
          ? !this.$v.form[key].$anyError
          : null
        : null;
    },
    isShowRequiedFeedbackText(dataKey) {
      return this.isErrorFeedbackText(dataKey, 'required');
    },

    //return true if NOT meet requirment
    isErrorFeedbackText(dataKey, errorKey) {
      return this.$v.form[dataKey].$dirty ? !this.$v.form[dataKey][`${errorKey}`] : false;
    },
    isNotAvailableField(dataKey) {
      return this.disableFields[dataKey];
    }
  }
};
</script>

<style lang="scss" scoped>
.text__error {
  color: #54371e;
  background-color: #faf3ed;
  padding: 0.5rem 2rem;
  border-radius: 10.4px;
  font-size: 1rem;
  gap: 10px;
  font-weight: 500;
}
.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
li.text__note--list {
  list-style-type: disc;
  margin: 0.5rem 0;
}
</style>
